import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';

import {
  useGetAdminTicketByIdQuery,
  useSendAdminMessageMutation,
  useUpdateTicketStatusMutation,
  useAddInternalNoteMutation,
  useDeleteTicketMessageMutation,
} from '../../store/api/ticketApi';
import { useSseConnection } from '../../hooks/useSseConnection';
import { API_BASE_URL } from '../../utils/constants';
import { Loader } from '../../components/ui/Loader';
import type { Ticket, TicketMessage, TicketAttachment, TicketInternalNote } from '../../store/api/ticketApi';

function getFileType(mimeType: string) {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  return 'other';
}

function getStatusColor(status: string) {
  switch (status) {
    case 'open':
      return 'text-green-500 bg-green-500/10';
    case 'pending':
      return 'text-yellow-500 bg-yellow-500/10';
    case 'closed':
      return 'text-gray-500 bg-gray-500/10';
    default:
      return 'text-gray-500 bg-gray-500/10';
  }
}

function getPriorityColor(priority: string) {
  switch (priority) {
    case 'high':
      return 'text-red-500 bg-red-500/10';
    case 'medium':
      return 'text-yellow-500 bg-yellow-500/10';
    case 'low':
      return 'text-blue-500 bg-blue-500/10';
    default:
      return 'text-gray-500 bg-gray-500/10';
  }
}

export function SupportTicketDetailsPage() {
  const { t, i18n } = useTranslation();
  const { ticketId } = useParams<{ ticketId: string }>();
  const [message, setMessage] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [attachments, setAttachments] = useState<FileList | null>(null);
  const [localMessages, setLocalMessages] = useState<TicketMessage[]>([]);
  const [localAttachments, setLocalAttachments] = useState<TicketAttachment[]>([]);
  const [localTicket, setLocalTicket] = useState<Ticket | null>(null);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isRTL = i18n.dir() === 'rtl';

  const { data, isLoading, error, refetch } = useGetAdminTicketByIdQuery(ticketId!);
  const [sendAdminMessage, { isLoading: isSending }] = useSendAdminMessageMutation();
  const [updateTicketStatus, { isLoading: isUpdatingStatus }] = useUpdateTicketStatusMutation();
  const [addInternalNote, { isLoading: isAddingNote }] = useAddInternalNoteMutation();
  const [deleteTicketMessage, { isLoading: isDeleting }] = useDeleteTicketMessageMutation();

  const ticket = localTicket || (data?.ticket as Ticket | undefined);
  const messages = localMessages.length > 0 ? localMessages : ((data?.messages || []) as TicketMessage[]);
  const allAttachments = localAttachments.length > 0 ? localAttachments : ((data?.attachments || []) as TicketAttachment[]);
  const internalNotes = (data?.internalNotes || []) as TicketInternalNote[];

  // Update local state when API data changes
  useEffect(() => {
    if (data?.messages) {
      setLocalMessages(data.messages);
    }
    if (data?.attachments) {
      setLocalAttachments(data.attachments);
    }
    if (data?.ticket) {
      setLocalTicket(data.ticket);
    }
  }, [data]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleDeleteMessage = async (messageId: string) => {
    if (confirm(t('support.ticketDetails.confirmDeleteMessage'))) {
      try {
        await deleteTicketMessage(messageId).unwrap();
        toast.success(t('support.ticketDetails.messageDeleted'));
      } catch (err) {
        console.error('Failed to delete message:', err);
        toast.error(t('support.ticketDetails.deleteMessageError'));
      }
    }
  };

  const handleTicketNotification = useCallback((notification: any) => {
    if (notification.ticketId !== ticketId) return;

    if (notification.type === 'new_message' && notification.message) {
      setLocalMessages((prev) => {
        // Check if message already exists
        if (prev.some(m => m._id === notification.message._id)) {
          return prev;
        }
        return [...prev, notification.message];
      });
      if (notification.attachments) {
        setLocalAttachments((prev) => {
          // Add new attachments
          const newAttachments = notification.attachments.filter(
            (a: TicketAttachment) => !prev.some(pa => pa._id === a._id)
          );
          return [...prev, ...newAttachments];
        });
      }
    } else if (notification.type === 'status_updated' && notification.status) {
      setLocalTicket((prev) => {
        if (!prev) return prev;
        return { ...prev, status: notification.status };
      });
    } else if (notification.type === 'message_deleted' && notification.messageId) {
      setLocalMessages((prev) => prev.filter(m => m._id !== notification.messageId));
      setLocalAttachments((prev) => prev.filter(a => a.message !== notification.messageId));
    }
  }, [ticketId]);

  // Connect to ticket SSE
  const { connect } = useSseConnection({
    autoConnect: false,
    onTicketNotification: handleTicketNotification,
    sseUrl: `${API_BASE_URL}/tickets/sse`,
  });

  useEffect(() => {
    connect(`${API_BASE_URL}/tickets/sse`);
  }, [connect]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() && (!attachments || attachments.length === 0)) return;

    try {
      await sendAdminMessage({
        ticketId: ticketId!,
        content: message,
        attachments: attachments || undefined,
      }).unwrap();
      setMessage('');
      setAttachments(null);
      refetch();
      toast.success(t('support.ticketDetails.messageSent'));
    } catch (err) {
      console.error('Failed to send message:', err);
      toast.error(t('support.ticketDetails.messageError'));
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      await updateTicketStatus({
        ticketId: ticketId!,
        status: newStatus,
      }).unwrap();
      refetch();
      toast.success(t('support.ticketDetails.statusUpdated'));
    } catch (err) {
      console.error('Failed to update status:', err);
      toast.error(t('support.ticketDetails.statusError'));
    }
  };

  const handleAddInternalNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!internalNote.trim()) return;

    try {
      await addInternalNote({
        ticketId: ticketId!,
        content: internalNote,
      }).unwrap();
      setInternalNote('');
      refetch();
      toast.success(t('support.ticketDetails.noteAdded'));
    } catch (err) {
      console.error('Failed to add note:', err);
      toast.error(t('support.ticketDetails.noteError'));
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getMessageAttachments = (msgId: string) => {
    return allAttachments.filter(att => att.message === msgId);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-6xl mx-auto">
            <div className="flex justify-center items-center py-16">
              <Loader />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-6xl mx-auto">
            <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
              <p className="text-red-400 text-lg mb-4">{t('support.ticketDetails.notFound')}</p>
              <Link
                to="/support/tickets"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-xl hover:shadow-lg hover:shadow-brand-accent/50 transition"
              >
                {t('support.ticketDetails.backToList')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      <div className="container mx-auto px-4 py-16 content-overlay">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-8 animate-slide-up">
            <Link
              to="/support/tickets"
              className="inline-flex items-center gap-2 text-neutral-medium hover:text-brand-accent transition mb-4"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {t('support.ticketDetails.backToList')}
            </Link>

            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div>
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-brand-accent mb-4">
                  {ticket.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(ticket.status)}`}>
                    {t(`tickets.status.${ticket.status}`)}
                  </span>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${getPriorityColor(ticket.priority)}`}>
                    {t(`tickets.priority.${ticket.priority}`)}
                  </span>
                  <span className="text-neutral-medium text-sm">
                    {t('support.ticketDetails.category')}: {t(`tickets.category.${ticket.category}`)}
                  </span>
                </div>
                <p className="text-neutral-medium text-sm">
                  {t('support.ticketDetails.user')}:{' '}
                  {ticket.user?._id ? (
                    <Link
                      to={`/admin/users/${ticket.user._id}`}
                      className="text-brand-accent hover:underline"
                    >
                      {ticket.user.firstName} {ticket.user.lastName}
                      {ticket.user.email && ` (${ticket.user.email})`}
                    </Link>
                  ) : (
                    <>
                      {ticket.user?.firstName} {ticket.user?.lastName}
                      {ticket.user?.email && ` (${ticket.user.email})`}
                    </>
                  )}
                  {ticket.user?.telegramId && ` • Telegram ID: ${ticket.user.telegramId}`}
                </p>
              </div>

              {/* Status Change */}
              <div className="flex items-center gap-2">
                <select
                  value={ticket.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  disabled={isUpdatingStatus}
                  className="px-4 py-2 rounded-xl border border-neutral/20 bg-neutral/5 text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent/50"
                >
                  <option value="open">{t('tickets.status.open')}</option>
                  <option value="pending">{t('tickets.status.pending')}</option>
                  <option value="closed">{t('tickets.status.closed')}</option>
                </select>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="card-glass rounded-2xl shadow-2xl mb-6 overflow-hidden animate-scale-in">
            <div className="p-4 md:p-6 max-h-[60vh] overflow-y-auto space-y-4">
              {messages.map((msg: TicketMessage) => (
                  <div
                    key={msg._id}
                    className={`flex ${msg.senderType === 'user' ? (isRTL ? 'justify-start' : 'justify-end') : (isRTL ? 'justify-end' : 'justify-start')}`}
                  >
                    <div className={`max-w-[80%] ${msg.senderType === 'user' ? (isRTL ? 'items-start' : 'items-end') : (isRTL ? 'items-end' : 'items-start')} flex flex-col`}>
                      <div className="relative">
                        <div className={`ticket-chat-bubble px-4 py-3 rounded-2xl ${
                          msg.senderType === 'user'
                            ? 'ticket-chat-bubble--user ' + (isRTL ? 'rounded-bl-md' : 'rounded-br-md')
                            : 'ticket-chat-bubble--support ' + (isRTL ? 'rounded-br-md' : 'rounded-bl-md')
                        }`}>
                        <p className="font-medium mb-1">
                          {msg.senderType === 'user'
                            ? msg.sender?.firstName || t('support.ticketDetails.userLabel')
                            : msg.sender?.firstName || t('support.ticketDetails.support')}
                        </p>
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                        </div>
                        
                        {/* Delete button */}
                        <button
                          onClick={() => handleDeleteMessage(msg._id)}
                          disabled={isDeleting}
                          className="absolute top-2 right-2 text-red-400 hover:text-red-300 transition disabled:opacity-50"
                          title={t('support.ticketDetails.deleteMessage') || 'Delete message'}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    
                    {/* Attachments */}
                    {getMessageAttachments(msg._id).length > 0 && (
                      <div className="mt-2 space-y-2">
                        {getMessageAttachments(msg._id).map((att: TicketAttachment) => {
                          const fileType = getFileType(att.mimeType);
                          const src = `/api/v1/support/attachments/${att._id}/download`;
                          if (fileType === 'image') {
                            return (
                              <button
                                key={att._id}
                                type="button"
                                onClick={() => setLightboxSrc(src)}
                                className="block overflow-hidden rounded-xl max-w-xs"
                              >
                                <img
                                  src={src}
                                  alt={att.originalName}
                                  className="w-full h-auto object-cover rounded-xl hover:scale-105 transition-transform duration-200 cursor-pointer"
                                />
                              </button>
                            );
                          }
                          if (fileType === 'video') {
                            return (
                              <div key={att._id} className="max-w-xs">
                                <video
                                  src={src}
                                  controls
                                  preload="metadata"
                                  className="w-full rounded-xl cursor-pointer"
                                  onClick={() => setLightboxSrc(src)}
                                />
                              </div>
                            );
                          }
                          return (
                            <a
                              key={att._id}
                              href={src}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm ${
                                msg.senderType === 'user'
                                  ? 'bg-neutral/10 text-neutral-light'
                                  : 'bg-brand-accent/20 text-brand-accent'
                              }`}
                            >
                              <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                              </svg>
                              <span className="truncate">{att.originalName}</span>
                            </a>
                          );
                        })}
                      </div>
                    )}

                    <p className="text-xs text-neutral-medium mt-1">
                      {formatDate(msg.createdAt)}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Send Message Form */}
          {ticket.status !== 'closed' && (
            <form onSubmit={handleSendMessage} className="space-y-4 mb-8 animate-scale-in">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t('support.ticketDetails.messagePlaceholder')}
                rows={3}
                className="w-full px-4 py-3 rounded-xl border border-neutral/20 bg-neutral/5 text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent/50 resize-none"
              />
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1">
                  <input
                    id="file-upload"
                    type="file"
                    multiple
                    onChange={(e) => setAttachments(e.target.files)}
                    className="sr-only"
                  />
                  <label
                    htmlFor="file-upload"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-neutral/20 bg-neutral/5 text-brand-accent hover:bg-brand-accent/10 transition cursor-pointer text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                    {i18n.language.startsWith('ar') ? 'ارفع ملف' : 'Choose Files'}
                  </label>
                </div>
                <button
                  type="submit"
                  disabled={isSending || (!message.trim() && (!attachments || attachments.length === 0))}
                  className="px-6 py-3 bg-brand-accent text-brand-primary font-semibold rounded-xl hover:shadow-lg hover:shadow-brand-accent/50 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSending ? (
                    <>
                      <Loader size="sm" />
                      {t('support.ticketDetails.sending')}
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      {t('support.ticketDetails.send')}
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Internal Notes */}
          <div className="card-glass rounded-2xl shadow-2xl border border-yellow-500/30 p-6 animate-scale-in bg-yellow-500/5">
            <h2 className="text-xl font-heading font-bold text-yellow-500 mb-4">
              {t('support.ticketDetails.internalNotes.title')}
            </h2>

            {/* Notes List */}
            <div className="mb-4 space-y-3">
              {internalNotes.map((note: TicketInternalNote) => (
                <div key={note._id} className="bg-yellow-500/5 rounded-xl p-4 border border-yellow-500/20">
                  <p className="text-sm text-neutral-light mb-1">
                    <strong>{note.createdBy.firstName} {note.createdBy.lastName}</strong> • {formatDate(note.createdAt)}
                  </p>
                  <p className="text-neutral-medium whitespace-pre-wrap">{note.content}</p>
                </div>
              ))}
              {internalNotes.length === 0 && (
                <p className="text-neutral-medium text-sm">{t('support.ticketDetails.internalNotes.noNotes')}</p>
              )}
            </div>

            {/* Add Note Form */}
            <form onSubmit={handleAddInternalNote} className="space-y-3">
              <textarea
                value={internalNote}
                onChange={(e) => setInternalNote(e.target.value)}
                placeholder={t('support.ticketDetails.internalNotes.placeholder')}
                rows={2}
                className="w-full px-4 py-3 rounded-xl border border-yellow-500/20 bg-yellow-500/5 text-neutral-light focus:outline-none focus:ring-2 focus:ring-yellow-500/50 resize-none"
              />
              <button
                type="submit"
                disabled={isAddingNote || !internalNote.trim()}
                className="px-6 py-2 bg-yellow-500 text-yellow-50 font-semibold rounded-xl hover:shadow-lg hover:shadow-yellow-500/30 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isAddingNote ? (
                  <>
                    <Loader size="sm" />
                    {t('support.ticketDetails.internalNotes.adding')}
                  </>
                ) : (
                  t('support.ticketDetails.internalNotes.add')
                )}
              </button>
            </form>
          </div>

          {/* Back Button */}
          <div className="text-center mt-6">
            <Link
              to="/support"
              className="inline-block px-6 py-3 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transform hover:scale-105 transition-all duration-300"
            >
              {t('common.back')}
            </Link>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxSrc(null)}
        >
          <button
            type="button"
            className="absolute top-4 right-4 text-white text-3xl hover:text-neutral-300 transition z-10"
            onClick={() => setLightboxSrc(null)}
          >
            ✕
          </button>
          <div className="max-w-full max-h-full" onClick={(e) => e.stopPropagation()}>
            {lightboxSrc.match(/\.(mp4|webm|ogg)/i) ? (
              <video src={lightboxSrc} controls autoPlay className="max-w-full max-h-[90vh] rounded-xl" />
            ) : (
              <img src={lightboxSrc} alt="" className="max-w-full max-h-[90vh] object-contain rounded-xl" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
